import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import Highlighter from 'react-highlight-words';
import CloseButtonSVG from '../../images/common/close.svg';
import GroupButtons from '../Parts/GroupButtons';
import { MAX_TAG_DEPTH } from '../../lib/constants';
import {
  Main,
  BaseDialog,
  Header,
  Contents,
  CloseButton,
  CloseButtonImage,
} from '../../styles/common/Dialog';
import ColorSF2 from '../../styles/common/ColorSF2';

const BodyBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px;
`;
const SearchBoxWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const SearchBoxInputField = styled.input`
  width: 400px;
  height: 32px;
  padding: 0 4px;
  font-size: 14px;
  border: 1px solid ${ColorSF2.mediumGray};
`;
const DepthButtonsWrapper = styled.div`
  margin: 18px 0px;
`;
const SearchResultWrapper = styled.div`
  width: 100%;
`;
const SearchResult = styled.div`
  margin-top: 18px;
  width: 100%;
  height: 270px;
  background: ${ColorSF2.lightGray};
  overflow-y: auto;
`;
const TagInfoWrapper = styled.div`
  border-top: 1px solid ${ColorSF2.white};
  padding: 10px;
`;
const TagInfoTitle = styled.p`
  font-size: 14px;
  font-weight: bold;
`;
// TODO １行表示で展開させる
const TagInfo = styled.p`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  overflow: hidden;
`;

const TagSearchDialog = ({ tags, selectedTagIds, onClose, select }) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const [filterWord, setFilterWord] = useState('');
  const [filterdResultList, setFilterdResultList] = useState([]);
  const [selectedDepth, setSelectedDepth] = useState([]);
  const buttonLabels = () => {
    const id = 'timeLine.timeLineSearchBox.select.depth';
    const items = [];
    for (let depth = 1; depth <= MAX_TAG_DEPTH; depth += 1) {
      items.push(formatMessage({ id }, { depth }));
    }
    return items;
  };
  useEffect(() => {
    if (!filterWord) {
      // 検索値が未設定の場合は結果に何も表示しない
      setFilterdResultList([]);
      return;
    }
    const result = tags
      .filter(tagArray => {
        const exist = tagArray.some((tag, index) => {
          const depth = index + 1;
          return (
            (selectedTagIds.length === 0 || selectedTagIds.length < depth) &&
            (selectedDepth.length === 0 || selectedDepth.includes(depth)) &&
            tag.name.includes(filterWord)
          );
        });
        return exist;
      })
      .filter(tagArray => {
        // 全て一致する
        const exist = selectedTagIds.every(
          (id, index) => tagArray.length > index && tagArray[index].id === id,
        );
        return exist;
      });
    setFilterdResultList(result);
  }, [tags, filterWord, selectedDepth, selectedTagIds, setFilterdResultList]);
  return (
    <Main
      id="closeTagSearchDialogDialog"
      role="presentation"
      onClick={() => onClose()}
    >
      <BaseDialog role="presentation" onClick={e => e.stopPropagation()}>
        <Contents>
          <Header>
            <FormattedMessage id="timeLine.tagSearch.title" />
            <CloseButton
              id="closeButton"
              role="presentation"
              className="button"
              onClick={() => onClose()}
            >
              <CloseButtonImage
                src={CloseButtonSVG}
                alt="Close TagSearchDialog"
              />
            </CloseButton>
          </Header>
          <BodyBlock>
            <SearchBoxWrapper>
              <SearchBoxInputField
                id="SetGroupDialogTextField"
                type="text"
                value={filterWord}
                placeholder={formatMessage(
                  { id: 'common.placeholder' },
                  {
                    name: formatMessage({
                      id: 'common.placeholder.item.keyword',
                    }),
                  },
                )}
                onChange={e => setFilterWord(e.target.value)}
              />
            </SearchBoxWrapper>
            <DepthButtonsWrapper>
              <GroupButtons
                length={buttonLabels().length}
                labels={buttonLabels()}
                selected={selectedDepth}
                setSelected={setSelectedDepth}
              />
            </DepthButtonsWrapper>
            {filterWord && (
              <SearchResultWrapper>
                <FormattedMessage
                  id="common.message.search.result"
                  values={{ count: filterdResultList.length }}
                />
                <SearchResult>
                  {filterdResultList.map((tagsInfo, index) => {
                    const key = index;
                    const last = tagsInfo[tagsInfo.length - 1];
                    const params = tagsInfo.map(({ id: tagId }, index2) => ({
                      tagId,
                      depth: index2 + 1,
                    }));
                    return (
                      <TagInfoWrapper
                        key={key}
                        className="clickableItem"
                        onClick={() => select(params)}
                      >
                        <TagInfoTitle>{last.name}</TagInfoTitle>
                        <TagInfo>
                          {tagsInfo.map(({ name }, index2) => {
                            const showSymbol = index2 < tagsInfo.length - 1;
                            const highlight =
                              selectedDepth.length === 0 ||
                              selectedDepth.includes(index2 + 1);
                            const key2 = `tagname-${index}-${index2}`;
                            return (
                              <Fragment key={key2}>
                                {(highlight && (
                                  <Highlighter
                                    searchWords={filterWord.split(/\s/)}
                                    autoEscape
                                    textToHighlight={name}
                                    highlightStyle={{ background: '#FFFABE' }}
                                  />
                                )) ||
                                  name}
                                {showSymbol && ' > '}
                              </Fragment>
                            );
                          })}
                        </TagInfo>
                      </TagInfoWrapper>
                    );
                  })}
                </SearchResult>
              </SearchResultWrapper>
            )}
          </BodyBlock>
        </Contents>
      </BaseDialog>
    </Main>
  );
};

TagSearchDialog.propTypes = {
  tags: PropTypes.array.isRequired,
  selectedTagIds: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  select: PropTypes.func.isRequired,
};

export default TagSearchDialog;
